<template>
    <app-layout>
        <page-header :title="trans('assets.overview.headline')" title-in-app-header>
            <div class="mt-3 flex flex-wrap items-start gap-3">
                <filter-bar v-model="queryParams" :config="filterBarConfig" class="min-w-0 flex-1">
                    <template #locations-option="{ option }">
                        <div class="flex items-center justify-between gap-3">
                            <span>{{ option.label }}</span>
                            <small class="text-muted">{{ option.iataCode }}</small>
                        </div>
                    </template>
                    <template #operabilities-option="{ option }">
                        <div class="flex items-center justify-between" style="gap: 0.5rem">
                            <span>{{ option.label }}</span>
                            <em :class="option.value" class="operability-indicator" />
                        </div>
                    </template>
                    <template #types-option="{ option }">
                        <div class="flex items-center justify-between gap-3">
                            <span>{{ option.label }}</span>
                            <small class="text-muted">{{ option.shortLabel }}</small>
                        </div>
                    </template>
                </filter-bar>
                <modal-link
                    v-if="hasAnyPermission('asset.create')"
                    :href="route('assets.quick-onboard.create')"
                    class="btn btn-secondary sm-full-width relative shrink-0 text-nowrap"
                    max-width="lg"
                >
                    {{ trans('assets.details.create_new_asset') }}
                </modal-link>
            </div>
        </page-header>

        <single-column-table-view
            v-if="isMobile"
            :fields="fields"
            :items="transformedAssets"
            :query-params="queryParams"
            @row-clicked="showDetails"
        />
        <div v-else>
            <data-table
                :columns="fields"
                :items="transformedAssets"
                :no-items-view="noItemsView"
                :query-params="queryParams"
                :selection-enabled="hasFeature('asset-batch-operations') && $page.props.bulkActions.length > 0 ? 'global-assets' : false"
                @row-clicked="showDetails"
            >
                <template #manufacturer="{ item }">
                    <td>
                        {{ item.manufacturer }}
                    </td>
                </template>

                <template #type="{ item }">
                    <td>
                        <div>{{ item.type }}</div>
                    </td>
                </template>

                <template #designation="{ item }">
                    <td>
                        <div>
                            {{ item.designation }}
                        </div>
                        <small class="text-dark text-nowrap">{{ trans('assets.overview.cell.serial_number') }}: {{ item.serialNumber }}</small>
                    </td>
                </template>

                <template #location="{ item }">
                    <td>
                        {{ item.location.iataCode }}
                    </td>
                </template>
                <template #specific_location="{ item }">
                    <specific-location-overview-cell :location="item.location" />
                </template>
                <template #area="{ item }">
                    <area-location-overview-cell :location="item.location" />
                </template>
                <template #status="{ item }">
                    <td>
                        <div>
                            <div class="align-items-top flex justify-between gap-1">
                                <status-badge :operability="item.status.operability" :usage="item.status.usageCategory" />
                                <Link
                                    v-if="item.status.workOrdersInProgress > 0"
                                    :href="route('assets.workorders.index', item.id)"
                                    class="mt-1"
                                    @click.stop=""
                                >
                                    <work-order-badge
                                        v-c-tooltip="{
                                            content: trans('assets.overview.tooltip.work_orders_in_progress', {
                                                '%d': item.status.workOrdersInProgress,
                                            }),
                                        }"
                                        :count="item.status.workOrdersInProgress"
                                    />
                                </Link>
                            </div>
                            <small v-if="item.status.usageCategory === 'spare'" class="d-block m-1 text-nowrap text-muted">
                                {{ trans('assets.status.' + item.status.usageCategory) }}
                            </small>
                            <small
                                v-if="item.status.hasOwnProperty('lastPerformedTestAt') && hasAnyPermission('asset.status_check.read')"
                                class="d-block m-1 text-nowrap text-muted"
                            >
                                <status-check-date :multiline="true" :status="item.status" :timezone="item.timezone" />
                            </small>
                        </div>
                    </td>
                </template>

                <template #issues="{ item }">
                    <td>
                        <div class="align-middle">
                            <div v-if="item.status.usageCategory === 'not_present'">
                                <badge class="text-uppercase" color="warning">{{ trans('assets.status.not_present') }}</badge>
                            </div>
                            <div v-if="item.status.isPendingCommissioning">
                                <badge class="text-uppercase" color="warning">
                                    {{ trans('activity.asset.operability.changed.reason_for_change.initial_commissioning_pending') }}
                                </badge>
                            </div>
                            <div v-for="issue in item.issues">
                                <issue-badge :issue="issue" />
                            </div>
                            <div v-if="item.scores">
                                <div
                                    v-for="(score, label) in item.scores.data"
                                    :key="label"
                                    class="text-uppercase flex justify-between text-sm text-muted"
                                >
                                    <div class="mr-2">
                                        <small>{{ label }}:</small>
                                    </div>
                                    <div>
                                        <small>{{ score }}</small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </td>
                </template>
            </data-table>
        </div>
        <batch-jobs v-if="hasFeature('asset-batch-operations')" :query-params="queryParams?.urlParams()" :selection="selection" />
    </app-layout>
</template>
<script>
import AppLayout from '@/Layouts/AppLayout.vue';
import Pagination from '@/Components/Pagination.vue';
import FilterBar from '@/Components/FilterBar.vue';
import IssueBadge from '@/Components/IssueBadge.vue';
import Badge from '@/Components/Badge.vue';
import StatusBadge from '@/Components/StatusBadge.vue';
import StatusCheckDate from '@/Components/StatusCheckDate.vue';
import BatchJobs from '@/Pages/Assets/BatchJobs/BatchJobs.vue';
import OnboardedAssetIssue from '@/Pages/Assets/OnboardedAssetIssue.vue';
import WorkOrderBadge from '@/Components/WorkorderBadge.vue';
import JetCheckbox from '@/Jetstream/Checkbox.vue';
import DataTable from '@/Components/DataTable.vue';
import { Link, usePage } from '@inertiajs/vue3';
import PageHeader from '@/Components/PageHeader.vue';
import SingleColumnTableView from '@/Pages/SingleColumnTableView.vue';
import useSelectedItems from '@/Utils/UseSelectedItems';
import { isDesktop, isMobile } from '@/Utils/UseMedia';
import SpecificLocationOverviewCell from '@/Components/SpecificLocationOverviewCell.vue';
import AreaLocationOverviewCell from '@/Components/AreaLocationOverviewCell.vue';
import ModalLink from '@/Components/ModalLink.vue';
import { when } from '@/Utils/when';
import { computed, ref } from 'vue';
import { trans } from '@/mixins/i18n.js';

export default {
    components: {
        AreaLocationOverviewCell,
        SpecificLocationOverviewCell,
        SingleColumnTableView,
        PageHeader,
        DataTable,
        JetCheckbox,
        OnboardedAssetIssue,
        WorkOrderBadge,
        BatchJobs,
        AppLayout,
        Pagination,
        Badge,
        IssueBadge,
        FilterBar,
        StatusBadge,
        StatusCheckDate,
        Link,
        ModalLink,
    },

    setup() {
        const page = usePage();

        const queryParams = ref(null);

        const pageFilters = computed(() => page.props.filters ?? {});

        const locations = computed(() =>
            (pageFilters.value.locations?.data || []).map((loc) => ({
                label: loc.name,
                value: loc.icaoCode,
                iataCode: loc.iataCode,
                searchableContent: `${loc.iataCode}|${loc.icaoCode}|${loc.name}`,
            })),
        );
        const specificLocations = computed(() =>
            (pageFilters.value.specificLocations?.data || []).map((loc) => ({ label: loc.name, value: loc.name })),
        );
        const areas = computed(() => (pageFilters.value.areas?.data || []).map((area) => ({ label: area.name, value: area.id })));
        const types = computed(() => pageFilters.value.types?.data || []);
        const usages = computed(() => pageFilters.value.usages?.data || []);
        const operabilities = computed(() => pageFilters.value.operabilities?.data || []);

        const filterBarConfig = computed(() => {
            const filters = [
                {
                    type: 'search',
                    key: 'search',
                    placeholder: trans('assets.overview.filter.placeholder'),
                },
                {
                    type: 'multi-select',
                    key: 'locations',
                    label: trans('dashboard.filter.locations'),
                    options: locations.value,
                    showSearchbar: true,
                },
                {
                    type: 'multi-select',
                    key: 'specificLocations',
                    label: trans('activities.overview.cell.specific_location'),
                    options: specificLocations.value,
                    showSearchbar: true,
                },
                {
                    type: 'multi-select',
                    key: 'areas',
                    label: trans('locations.area.label'),
                    options: areas.value,
                    showSearchbar: true,
                },
                {
                    type: 'multi-select',
                    key: 'usages',
                    label: trans('assets.overview.filter.usage'),
                    options: usages.value,
                },
                {
                    type: 'multi-select',
                    key: 'operabilities',
                    label: trans('assets.overview.filter.operability'),
                    options: operabilities.value,
                },
                {
                    type: 'multi-select',
                    key: 'types',
                    label: trans('assets.overview.filter.type'),
                    options: types.value,
                },
            ];

            return {
                route: route('assets.index'),
                routeOptions: { only: ['assets'] },
                defaultSort: { column: 'status', asc: false },
                filters,
            };
        });

        const assetsMeta = computed(() => page.props.assets?.meta || {});
        const selection = useSelectedItems('global-assets', assetsMeta);

        return {
            filterBarConfig,
            queryParams,
            isMobile,
            isDesktop,
            selection,
        };
    },

    data() {
        return {
            noItemsView: {
                noItems: this.trans('assets.overview.results.empty'),
            },
            isSearching: false,
            selectedAsset: null,
        };
    },

    computed: {
        fields() {
            return [
                {
                    key: 'status',
                    label: this.trans('assets.overview.cell.status'),
                    _style: 'width: 10%',
                },
                {
                    key: 'type',
                    label: this.trans('assets.overview.cell.type'),
                    _style: 'width: 90px',
                },
                {
                    key: 'designation',
                    label: this.trans('assets.overview.cell.designation'),
                    _style: 'width: 20%',
                },
                when(this.$page.props.locations?.data?.length > 1, {
                    key: 'location',
                    label: this.trans('assets.overview.cell.airport'),
                    _style: 'width: 90px',
                }),
                {
                    key: 'specific_location',
                    label: this.trans('assets.overview.cell.location'),
                    _style: 'width: 15%; white-space: nowrap;',
                },
                when(this.assets?.meta.hasAreaLocation, {
                    key: 'area',
                    label: this.trans('locations.area.label'),
                    _style: 'width: 20%; white-space: nowrap; min-width: 8rem;',
                }),
                {
                    key: 'manufacturer',
                    label: this.trans('assets.overview.cell.manufacturer'),
                },
                {
                    key: 'issues',
                    label: this.trans('assets.overview.cell.issues'),
                    _style: 'width: 15%',
                },
            ].filter(Boolean);
        },
        assets() {
            return this.$page.props.assets;
        },
        transformedAssets() {
            const notAvailable = this.trans('general.not_available');

            if (!this.assets) {
                return null;
            }

            const { meta, links, data } = this.assets;

            return {
                meta,
                links,
                data: data.map((asset) => ({
                    id: asset.id,
                    type: asset.type.shortLabel,
                    designation: asset.designation ? asset.designation.label : notAvailable,
                    manufacturer: asset.manufacturer ? asset.manufacturer.name : notAvailable,
                    curieId: asset.curieId,
                    serialNumber: asset.serialNumber || notAvailable,
                    location: asset.location,
                    timezone: asset.timezone,
                    issues: asset.issues,
                    status: asset.status,
                    scores: asset.scores,
                })),
            };
        },
    },

    methods: {
        trans,
        showDetails(asset) {
            this.$inertia.visit(this.route('assets.show', asset.id));
        },
    },
};
</script>
